import React, { useEffect, useState } from "react";
import { UserItem } from "../../hooks/useUsers";
import { apiService } from "../../services/apiService";

interface EditUserModalProps {
  user: UserItem;
  isOpen: boolean;
  onClose: () => void;
  onSave: (updatedData: {
    trialStartedAt: string;
    trialEndDate: string;
    status: "Active" | "Inactive";
  }) => Promise<void>;
}

interface TaxAccessProperty {
  id: string;
  label: string;
  address?: string;
}

interface TaxAccessGrant {
  id?: string;
  taxYear: number;
  propertyId: string;
  propertyLabel: string;
  grantedAt?: string;
  grantedBy?: string;
  transactionId?: string;
  reason?: string;
}

interface TaxAccessResponse {
  success: boolean;
  message?: string;
  properties: TaxAccessProperty[];
  grants: TaxAccessGrant[];
}

export const EditUserModal: React.FC<EditUserModalProps> = ({
  user,
  isOpen,
  onClose,
  onSave,
}) => {
  const toInputDateFormat = (isoString: string | null) => {
    if (!isoString) return "";
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return "";
    return date.toISOString().split("T")[0];
  };

  const [trialStartedAt, setTrialStartedAt] = useState(
    toInputDateFormat(user.trialStartedAt),
  );
  const [trialEndDate, setTrialEndDate] = useState(
    toInputDateFormat(user.trialEndDate),
  );
  const [status, setStatus] = useState<"Active" | "Inactive">(user.status);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [taxAccessLoading, setTaxAccessLoading] = useState(false);
  const [taxAccessSaving, setTaxAccessSaving] = useState(false);
  const [taxAccessError, setTaxAccessError] = useState("");
  const [taxAccessProperties, setTaxAccessProperties] = useState<
    TaxAccessProperty[]
  >([]);
  const [taxAccessGrants, setTaxAccessGrants] = useState<TaxAccessGrant[]>([]);
  const [selectedTaxYear, setSelectedTaxYear] = useState(
    String(new Date().getFullYear()),
  );
  const [selectedPropertyId, setSelectedPropertyId] = useState("");
  const [taxAccessReason, setTaxAccessReason] = useState("");
  console.log("@@@@@@@-taxAccessGrants", taxAccessGrants);
  useEffect(() => {
    setTrialStartedAt(toInputDateFormat(user.trialStartedAt));
    setTrialEndDate(toInputDateFormat(user.trialEndDate));
    setStatus(user.status);
  }, [user]);

  useEffect(() => {
    if (!isOpen || !user?.id) return;

    let active = true;
    const loadTaxAccess = async () => {
      try {
        setTaxAccessLoading(true);
        setTaxAccessError("");
        const response = await apiService.get<TaxAccessResponse>(
          `portal-api/users/${user.id}/tax-access`,
        );
        if (!active) return;
        setTaxAccessProperties(response.properties || []);
        setTaxAccessGrants(response.grants || []);
        setSelectedPropertyId(response.properties?.[0]?.id || "");
      } catch (err: any) {
        if (!active) return;
        setTaxAccessError(err.message || "Failed to load tax report access.");
      } finally {
        if (active) setTaxAccessLoading(false);
      }
    };

    loadTaxAccess();
    return () => {
      active = false;
    };
  }, [isOpen, user?.id]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!trialStartedAt || !trialEndDate) {
      setError("Both Trial Start and Trial End dates are required.");
      return;
    }
    try {
      setSaving(true);
      setError("");
      await onSave({ trialStartedAt, trialEndDate, status });
    } catch (err: any) {
      setError(err.message || "Failed to save updates.");
    } finally {
      setSaving(false);
    }
  };

  const updateTaxAccess = async (
    action: "grant" | "revoke",
    grant?: TaxAccessGrant,
  ) => {
    const propertyId = grant?.propertyId || selectedPropertyId;
    const taxYear = Number(grant?.taxYear || selectedTaxYear);

    if (!propertyId) {
      setTaxAccessError("Select a property before granting tax report access.");
      return;
    }

    if (!Number.isInteger(taxYear)) {
      setTaxAccessError("Enter a valid tax year.");
      return;
    }

    try {
      setTaxAccessSaving(true);
      setTaxAccessError("");
      const payload = {
        action,
        propertyId,
        taxYear,
        reason: taxAccessReason,
      };
      console.log("@@@ POST tax-access payload:", payload);
      const response = await apiService.post<TaxAccessResponse>(
        `portal-api/users/${user.id}/tax-access`,
        payload,
      );
      console.log(
        "@@@ POST tax-access response grants:",
        response.grants?.map((g: any) => ({
          taxYear: g.taxYear,
          propertyId: g.propertyId,
          reason: g.reason,
          grantedBy: g.grantedBy,
        })),
      );

      setTaxAccessProperties(response.properties || []);
      setTaxAccessGrants(response.grants || []);
      setTaxAccessReason("");
    } catch (err: any) {
      setTaxAccessError(err.message || "Failed to update tax report access.");
    } finally {
      setTaxAccessSaving(false);
    }
  };

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center p-4'>
      {/* Backdrop */}
      <div
        className='fixed inset-0 bg-gray-900/40 backdrop-blur-sm transition-opacity animate-fade-in'
        onClick={onClose}
      />

      {/* Modal Box */}
      <div className='bg-white rounded-3xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6 relative z-10 animate-fade-in-scale'>
        {/* Header */}
        <div className='flex justify-between items-center mb-6'>
          <h3 className='text-xl font-bold text-gray-900'>Edit User Profile</h3>
          <button
            onClick={onClose}
            className='text-gray-400 hover:text-gray-600 transition-colors focus:outline-none'>
            <svg
              className='w-6 h-6'
              fill='none'
              stroke='currentColor'
              strokeWidth='2'
              viewBox='0 0 24 24'>
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                d='M6 18L18 6M6 6l12 12'
              />
            </svg>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className='space-y-4'>
          {error && (
            <div className='p-3 bg-red-50 border border-red-100 text-red-600 rounded-xl text-xs font-semibold animate-fade-in'>
              {error}
            </div>
          )}

          {/* User Name (Read-Only) */}
          <div>
            <label className='block text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5'>
              Full Name
            </label>
            <input
              type='text'
              value={user.name}
              disabled
              className='w-full px-4 py-2.5 border border-gray-100 rounded-xl bg-gray-50 text-gray-500 text-sm font-semibold cursor-not-allowed focus:outline-none'
            />
          </div>

          {/* User Email (Read-Only) */}
          <div>
            <label className='block text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5'>
              Email Address
            </label>
            <input
              type='text'
              value={user.email}
              disabled
              className='w-full px-4 py-2.5 border border-gray-100 rounded-xl bg-gray-50 text-gray-500 text-sm font-semibold cursor-not-allowed focus:outline-none'
            />
          </div>

          {/* Trial Start Date */}
          <div>
            <label className='block text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5'>
              Trial Start Date
            </label>
            <input
              type='date'
              value={trialStartedAt}
              onChange={(e) => setTrialStartedAt(e.target.value)}
              required
              className='w-full px-4 py-2.5 border border-gray-200 rounded-xl text-gray-800 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-orange-400 transition-all bg-white'
            />
          </div>

          {/* Trial End Date */}
          <div>
            <label className='block text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5'>
              Trial End Date
            </label>
            <input
              type='date'
              value={trialEndDate}
              onChange={(e) => {
                const date = e.target.value;
                setTrialEndDate(date);
                const givenDate = new Date(date);
                const today = new Date();

                today.setHours(0, 0, 0, 0);
                givenDate.setHours(0, 0, 0, 0);

                if (givenDate < today) {
                  setStatus("Inactive");
                }
              }}
              required
              className='w-full px-4 py-2.5 border border-gray-200 rounded-xl text-gray-800 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-orange-400 transition-all bg-white'
            />
          </div>

          {/* Account Status */}
          <div>
            <label className='block text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5'>
              Account Status
            </label>
            <div className='relative'>
              <select
                value={status}
                onChange={(e) =>
                  setStatus(e.target.value as "Active" | "Inactive")
                }
                className='w-full px-4 py-2.5 border border-gray-200 rounded-xl text-gray-800 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-orange-400 transition-all bg-white appearance-none cursor-pointer'>
                <option value='Active'>Active</option>
                <option value='Inactive'>Inactive</option>
              </select>
              <div className='pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-400'>
                <svg
                  className='fill-current h-4 w-4'
                  xmlns='http://www.w3.org/2000/svg'
                  viewBox='0 0 20 20'>
                  <path d='M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z' />
                </svg>
              </div>
            </div>
          </div>

          {/* Tax Report Access */}
          <div className='border border-orange-100 rounded-2xl p-4 bg-orange-50/30 space-y-3'>
            <div>
              <h4 className='text-sm font-bold text-gray-900'>
                Tax Report Access
              </h4>
              <p className='text-xs text-gray-500 mt-1'>
                Grant free tax report download for a specific property and year.
              </p>
            </div>

            {taxAccessError && (
              <div className='p-3 bg-red-50 border border-red-100 text-red-600 rounded-xl text-xs font-semibold'>
                {taxAccessError}
              </div>
            )}

            {taxAccessLoading ? (
              <div className='flex items-center gap-2 text-xs font-semibold text-gray-500'>
                <div className='animate-spin rounded-full h-4 w-4 border-2 border-orange-400 border-t-transparent' />
                Loading tax access...
              </div>
            ) : (
              <>
                <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
                  <div>
                    <label className='block text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5'>
                      Property
                    </label>
                    <select
                      value={selectedPropertyId}
                      onChange={(e) => setSelectedPropertyId(e.target.value)}
                      className='w-full px-3 py-2.5 border border-gray-200 rounded-xl text-gray-800 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-orange-400 bg-white'>
                      {taxAccessProperties.length === 0 ? (
                        <option value=''>No properties found</option>
                      ) : (
                        taxAccessProperties.map((property) => (
                          <option key={property.id} value={property.id}>
                            {property.label}
                          </option>
                        ))
                      )}
                    </select>
                  </div>
                  <div>
                    <label className='block text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5'>
                      Tax Year
                    </label>
                    <input
                      type='number'
                      min='2020'
                      max={new Date().getFullYear() + 1}
                      value={selectedTaxYear}
                      onChange={(e) => setSelectedTaxYear(e.target.value)}
                      className='w-full px-3 py-2.5 border border-gray-200 rounded-xl text-gray-800 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-orange-400 bg-white'
                    />
                  </div>
                </div>

                <div>
                  <label className='block text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5'>
                    Note
                  </label>
                  <input
                    type='text'
                    value={taxAccessReason}
                    onChange={(e) => setTaxAccessReason(e.target.value)}
                    placeholder='Optional reason for this free grant'
                    className='w-full px-3 py-2.5 border border-gray-200 rounded-xl text-gray-800 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-orange-400 bg-white'
                  />
                </div>

                <button
                  type='button'
                  disabled={
                    taxAccessSaving ||
                    !selectedPropertyId ||
                    taxAccessProperties.length === 0 ||
                    !taxAccessReason
                  }
                  onClick={() => updateTaxAccess("grant")}
                  className='w-full bg-[#E27D00] hover:bg-[#C96E00] text-white font-semibold py-2.5 rounded-xl text-sm transition-all shadow-sm focus:outline-none disabled:opacity-50'>
                  {taxAccessSaving ? "Updating..." : "Grant Free Tax Report"}
                </button>

                <div className='space-y-2'>
                  <p className='text-xs font-bold text-gray-400 uppercase tracking-wider'>
                    Active Tax Report Access
                  </p>
                  {taxAccessGrants.length === 0 ? (
                    <p className='text-xs text-gray-500 bg-white border border-gray-100 rounded-xl p-3'>
                      No tax report access granted yet.
                    </p>
                  ) : (
                    taxAccessGrants.map((grant) => {
                      const isRevocable =
                        grant.grantedBy === "manual_grant" ||
                        grant.grantedBy === "legacy_user_access";
                      return (
                        <div
                          key={`${grant.propertyId}-${grant.taxYear}`}
                          className='flex items-center justify-between gap-3 bg-white border border-gray-100 rounded-xl p-3'>
                          <div className='min-w-0'>
                            <p className='text-xs font-bold text-gray-900 truncate'>
                              {grant.propertyLabel}
                            </p>
                            <p className='text-xs text-gray-500'>
                              {grant.taxYear} tax report
                              {grant.grantedBy
                                ? ` • ${grant.grantedBy.replace(/_/g, " ")}`
                                : ""}
                            </p>
                            <p className='text-xs text-gray-500'>
                              Note: {grant?.reason || "None"}
                            </p>
                          </div>
                          {isRevocable ? (
                            <button
                              type='button'
                              disabled={taxAccessSaving}
                              onClick={() => updateTaxAccess("revoke", grant)}
                              className='shrink-0 text-xs font-bold text-red-600 hover:text-red-700 disabled:opacity-50'>
                              Revoke
                            </button>
                          ) : (
                            <span className='shrink-0 text-[10px] font-bold uppercase tracking-wider text-gray-400'>
                              Paid
                            </span>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </>
            )}
          </div>

          {/* Footer Actions */}
          <div className='flex gap-3 pt-4 border-t border-gray-100'>
            <button
              type='button'
              onClick={onClose}
              disabled={saving}
              className='flex-1 border border-gray-200 hover:bg-gray-50 text-gray-700 font-semibold py-2.5 rounded-xl text-sm transition-all focus:outline-none disabled:opacity-50'>
              Cancel
            </button>
            <button
              type='submit'
              disabled={saving}
              className='flex-1 bg-[#E27D00] hover:bg-[#C96E00] text-white font-semibold py-2.5 rounded-xl text-sm transition-all shadow-sm focus:outline-none flex items-center justify-center gap-2 disabled:opacity-50'>
              {saving ? (
                <>
                  <div className='animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent' />
                  Saving...
                </>
              ) : (
                "Save Changes"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
